import { appLogger } from '@bco/app-logger';
import {
  BascCenter,
  BascLoader,
  BascProvider,
  MantineThemeOverride,
} from '@bco/ui';
import { useState, useEffect, ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { loadClientTheme } from '../federationInit';

const CLIENT_MODULES_CSS = '/client-modules/assets/style.css';
let cssLoaded = false;

// The remote's CSS is bundled into a single static `/client-modules/assets/style.css`
// (cssCodeSplit:false). That file is only emitted when the remote is a BUILT artifact,
// and nothing references it automatically — the host must <link> it at runtime. This is
// needed in two cases, both of which serve the built remote:
//   - PROD: nginx serves the built remote; the host is also a build (`import.meta.env.PROD`).
//   - DEV TUNNEL: the host is a dev server, but the remote is `vite build --watch` served
//     by `vite preview` under `/client-modules/` (`VITE_DEV_TUNNEL=true`). See docs/22.
// In classic localhost dev (both false) the remote is a Vite dev server that injects its
// own CSS via JS, so this link must NOT be added — leaving that path byte-for-byte unchanged.
const remoteIsBuiltArtifact =
  import.meta.env.PROD ||
  (import.meta.env.VITE_DEV_TUNNEL ?? '').toLowerCase() === 'true';

function loadRemoteCss() {
  if (cssLoaded || !remoteIsBuiltArtifact) {
    return;
  }
  cssLoaded = true;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = CLIENT_MODULES_CSS;
  document.head.appendChild(link);
}

interface ClientThemeProviderProps {
  children: ReactNode;
}

/**
 * ClientThemeProvider - Dynamically loads and applies client-specific theme
 * based on the clientName extracted from the URL.
 * Theme CSS is bundled with the federated theme module (via import in theme/index.ts).
 */
export function ClientThemeProvider({ children }: ClientThemeProviderProps) {
  const { clientName } = useParams<{ clientName: string }>();
  const [theme, setTheme] = useState<MantineThemeOverride | undefined>(
    undefined
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!clientName) {
      setLoading(false);
      return;
    }

    async function loadTheme() {
      try {
        loadRemoteCss();
        const themeModule = await loadClientTheme(clientName!);

        if (themeModule) {
          setTheme(themeModule.default || themeModule.themeConfig?.theme);
        }
      } catch (error) {
        appLogger.error('failed to load client theme', {
          clientName,
          error: error instanceof Error ? error.message : String(error),
        });
      } finally {
        setLoading(false);
      }
    }

    loadTheme();
  }, [clientName]);

  if (loading) {
    return (
      <BascProvider colorScheme="auto">
        <BascCenter style={{ height: '100vh' }}>
          <BascLoader size="lg" />
        </BascCenter>
      </BascProvider>
    );
  }

  return (
    <BascProvider theme={theme} colorScheme="auto">
      {children}
    </BascProvider>
  );
}
